import { create } from "zustand"
import type {
    AvatarModel,
    AvatarPolarity,
    PlayerSnapshot,
    QuaternionTuple,
    Vector3Tuple,
} from "@domo/shared"

// Sprint 3 — Extensión cliente: agregamos timestamps locales de la
// animación de descenso. El server solo emite serverTs; los clientes
// guardan performance.now() del momento en que recibieron el evento
// para sincronizar la animación con el clock local del rAF.
export interface NetworkPeer extends PlayerSnapshot {
    descentStartedAtClient?: number
    descentMs?: number
    materializeMs?: number
}

interface NetworkState {
    selfId: string | null
    roomId: string | null
    peers: Map<string, NetworkPeer>
    serverPing: number | null

    setSelfId: (id: string | null) => void
    setRoom: (id: string | null) => void
    setPeers: (peers: PlayerSnapshot[]) => void
    addPeer: (peer: PlayerSnapshot) => void
    removePeer: (id: string) => void
    updatePeerTransform: (
        id: string,
        position: Vector3Tuple,
        rotation: QuaternionTuple
    ) => void
    updatePeerTarget: (id: string, target: Vector3Tuple | null) => void
    updatePeerIndex: (id: string, siliconIndex: number) => void
    updatePeerAnchor: (id: string, isAnchored: boolean) => void
    // Sprint 3/5 — registra animación de descenso de un peer remoto.
    beginPeerDescent: (
        id: string,
        model: AvatarModel,
        polarity: AvatarPolarity,
        position: Vector3Tuple,
        descentMs: number,
        materializeMs: number
    ) => void
    setPing: (ms: number | null) => void
    reset: () => void
}

export const useNetworkStore = create<NetworkState>((set) => ({
    selfId: null,
    roomId: null,
    peers: new Map(),
    serverPing: null,

    setSelfId: (selfId) => set({ selfId }),
    setRoom: (roomId) => set({ roomId }),
    setPeers: (peers) =>
        set({ peers: new Map(peers.map((p) => [p.id, p])) }),

    addPeer: (peer) =>
        set((s) => {
            const next = new Map(s.peers)
            next.set(peer.id, peer)
            return { peers: next }
        }),

    removePeer: (id) =>
        set((s) => {
            if (!s.peers.has(id)) return s
            const next = new Map(s.peers)
            next.delete(id)
            return { peers: next }
        }),

    updatePeerTransform: (id, position, rotation) =>
        set((s) => {
            const peer = s.peers.get(id)
            if (!peer) return s
            const next = new Map(s.peers)
            next.set(id, { ...peer, position, rotation })
            return { peers: next }
        }),

    updatePeerTarget: (id, target) =>
        set((s) => {
            const peer = s.peers.get(id)
            if (!peer) return s
            const next = new Map(s.peers)
            next.set(id, { ...peer, target })
            return { peers: next }
        }),

    updatePeerIndex: (id, siliconIndex) =>
        set((s) => {
            const peer = s.peers.get(id)
            if (!peer) return s
            const next = new Map(s.peers)
            next.set(id, { ...peer, siliconIndex })
            return { peers: next }
        }),

    updatePeerAnchor: (id, isAnchored) =>
        set((s) => {
            const peer = s.peers.get(id)
            if (!peer) return s
            const next = new Map(s.peers)
            next.set(id, { ...peer, isAnchored })
            return { peers: next }
        }),

    beginPeerDescent: (id, model, polarity, position, descentMs, materializeMs) =>
        set((s) => {
            const existing = s.peers.get(id)
            const next = new Map(s.peers)
            const base: NetworkPeer = existing ?? {
                id,
                displayName: "Tripulante",
                position,
                target: null,
                rotation: [0, 0, 0, 1],
                siliconIndex: 50,
                isAnchored: false,
                joinedAt: Date.now(),
                model,
                polarity,
                isIncarnated: true,
            }
            next.set(id, {
                ...base,
                model,
                polarity,
                isIncarnated: true,
                position,
                descentStartedAtClient: performance.now(),
                descentMs,
                materializeMs,
            })
            return { peers: next }
        }),

    setPing: (serverPing) => set({ serverPing }),

    reset: () =>
        set({
            selfId: null,
            roomId: null,
            peers: new Map(),
            serverPing: null,
        }),
}))
